import type { WebTestRun } from '@blume/web-test-model'
import { ArrowLeft, Check, Copy } from 'lucide-react'
import { isValidElement, useState, type ReactNode } from 'react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { relativeTime, shortSha } from '@/design/format'
import { classificationMeta, runVerdict, toneText } from '@/design/status'
import { cn } from '@/lib/utils'
import { RunAvatar, scenarioEmoji, type AvatarSize } from '@/ui/atoms/avatar-tile'

export function BackLink({
  href,
  label,
  className
}: {
  href: string
  label: string
  className?: string
}) {
  return (
    <a
      data-stacked-back-link
      href={href}
      className={cn(
        '-ml-3 mb-4 inline-flex h-9 items-center gap-1.5 rounded-md px-3',
        'text-[13px] font-medium text-muted-foreground transition-colors',
        'hover:bg-accent hover:text-foreground',
        'focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none',
        className
      )}
    >
      <ArrowLeft aria-hidden className="size-3.5" />
      {label}
    </a>
  )
}

export function PageTitle({ title, subtitle }: { title: string; subtitle?: ReactNode }) {
  return (
    <header className="mb-10">
      <h1 className="text-[28px] font-semibold tracking-tight text-balance">{title}</h1>
      {subtitle ? <p className="mt-1.5 text-sm text-muted-foreground">{subtitle}</p> : null}
    </header>
  )
}

export function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <h2 className="mb-1 text-xs font-medium tracking-[0.08em] text-muted-foreground uppercase">
      {children}
    </h2>
  )
}

/**
 * One level of containment for a grouped collection: a quiet hairline card
 * whose header names the group and whose content is the rows themselves.
 *
 * The rules that keep this from regressing into the rejected card design:
 * exactly one level — never render a GroupCard (or any other bordered
 * container) inside another; the surface is the stock `--card` step of the
 * ladder with the stock hairline border, no heavier stroke, no status
 * color. The only class-level deviation from the stock shadcn Card is
 * `rounded-lg` (the system's 6px radius instead of the stock 10px) and
 * tightened padding.
 *
 * When the whole card represents one addressable entity, `link` makes the
 * full surface navigate there. This is never a nested anchor: the card-level
 * destination is a positioned sibling overlay with its own accessible name,
 * and interactive rows inside the card (which are `relative`) stack above
 * it, so every link stays independently clickable and focusable. Hovering
 * the card tints the surface like a row hover; focusing the overlay draws
 * the standard focus ring around the card — no heavier outline.
 */
export function GroupCard({
  header,
  children,
  className,
  link
}: {
  header: ReactNode
  children: ReactNode
  className?: string
  link?: { href: string; label: string }
}) {
  return (
    <Card
      className={cn(
        'max-w-3xl gap-0 rounded-lg py-0',
        link &&
          'relative transition-colors has-[[data-card-link]:hover]:bg-accent/50 has-[[data-card-link]:focus-visible]:ring-[3px] has-[[data-card-link]:focus-visible]:ring-ring/50',
        className
      )}
    >
      {link ? (
        <a
          data-card-link
          href={link.href}
          aria-label={link.label}
          className="absolute inset-0 rounded-lg focus-visible:outline-none"
        />
      ) : null}
      <CardHeader className="gap-1 px-4 pt-4 sm:px-5 sm:pt-5">{header}</CardHeader>
      <CardContent className="px-4 pt-2 pb-2.5 sm:px-5 sm:pb-3">{children}</CardContent>
    </Card>
  )
}

export function EmptyState({ children }: { children: ReactNode }) {
  return <p className="py-12 text-center text-sm text-muted-foreground">{children}</p>
}

function metaItemKey(item: ReactNode): string {
  return isValidElement(item) && item.key !== null ? String(item.key) : String(item)
}

/** Interpunct-separated metadata line. */
export function MetaLine({ items }: { items: Array<ReactNode | undefined> }) {
  const visible = items.filter((item) => item !== undefined && item !== null && item !== '')
  return (
    <span className="text-[13px] text-muted-foreground">
      {visible.map((item, index) => (
        <span key={metaItemKey(item)}>
          {index > 0 ? <span aria-hidden> · </span> : null}
          {item}
        </span>
      ))}
    </span>
  )
}

export function sourceLabel(run: WebTestRun): string | undefined {
  if (run.source.pullRequest) return `PR #${run.source.pullRequest}`
  if (run.source.branch) return run.source.branch
  if (run.source.sha) return shortSha(run.source.sha)
  return undefined
}

/**
 * One run, one row, one link. The whole row is a single anchor — no nested
 * interactive controls. Healthy rows stay in muted ink; a failure's
 * classification is the only saturated text in the line.
 *
 * Editing rules: the meta line only repeats the verdict word when the
 * classification does not already say it, and it never carries actor or
 * duration — those live on the run detail. `hideSource` drops the PR/branch
 * when the surrounding group already names it. The age sits at the end of
 * the meta line rather than in a far-right column, so wide viewports don't
 * open a gulf between the text and the timestamp.
 */
export function RunRow({
  run,
  now,
  hideSource = false,
  hideScenario = false,
  avatarSize = 'regular'
}: {
  run: WebTestRun
  now?: number
  hideSource?: boolean
  hideScenario?: boolean
  avatarSize?: AvatarSize
}) {
  const verdict = runVerdict(run)
  const classification = run.classification ? classificationMeta[run.classification] : undefined
  const Icon = verdict.icon
  const quiet = verdict.tone === 'pass' || verdict.tone === 'neutral'
  const verdictIsRedundant =
    classification !== undefined &&
    (run.status === 'failed' || classification.label === verdict.label)
  return (
    <li>
      <a
        href={`#/runs/${encodeURIComponent(run.id)}`}
        className="group relative -mx-3 flex items-center gap-3 rounded-md px-3 py-3 transition-colors hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
      >
        {hideScenario ? (
          <Icon aria-hidden className={cn('mt-0.5 size-4 shrink-0', toneText[verdict.tone])} />
        ) : (
          <RunAvatar
            run={run}
            emoji={scenarioEmoji(run.scenarioId)}
            size={avatarSize}
            variant="corner"
          />
        )}
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
            <span className="font-mono text-[13px] font-semibold text-foreground">
              {hideScenario ? (sourceLabel(run) ?? run.id) : run.scenarioId}
            </span>
            {classification ? (
              <span className={cn('text-[13px] font-medium', toneText[classification.tone])}>
                {classification.label}
              </span>
            ) : null}
          </span>
          <span className="mt-0.5 block">
            <MetaLine
              items={[
                verdictIsRedundant ? undefined : verdict.label,
                run.state !== 'terminal' ? run.currentStageSummary : undefined,
                hideSource || hideScenario ? undefined : sourceLabel(run),
                relativeTime(run.createdAt, now)
              ]}
            />
          </span>
        </span>
      </a>
    </li>
  )
}

export function RunList({
  runs,
  now,
  hideSource = false,
  hideScenario = false,
  avatarSize = 'regular'
}: {
  runs: WebTestRun[]
  now?: number
  hideSource?: boolean
  hideScenario?: boolean
  avatarSize?: AvatarSize
}) {
  return (
    <ul className="max-w-[46rem] space-y-1">
      {runs.map((run) => (
        <RunRow
          key={run.id}
          run={run}
          now={now}
          hideSource={hideSource}
          hideScenario={hideScenario}
          avatarSize={avatarSize}
        />
      ))}
    </ul>
  )
}

export function CodeBlock({ children, copyText }: { children: ReactNode; copyText?: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <div className="flex items-center gap-2 rounded-md bg-muted py-1.5 pr-1.5 pl-3">
      <code className="min-w-0 flex-1 overflow-x-auto font-mono text-[13px] whitespace-nowrap">
        {children}
      </code>
      {copyText ? (
        <Button
          variant="ghost"
          size="icon"
          className="shrink-0"
          aria-label="Copy command"
          onClick={() => {
            void navigator.clipboard?.writeText(copyText).catch(() => undefined)
            setCopied(true)
            window.setTimeout(() => setCopied(false), 1_500)
          }}
        >
          {copied ? <Check aria-hidden /> : <Copy aria-hidden />}
        </Button>
      ) : null}
    </div>
  )
}

export function KeyValueList({
  items
}: {
  items: Array<{ key: string; value: ReactNode } | undefined>
}) {
  const visible = items.filter((item) => item !== undefined)
  return (
    <dl className="grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-2">
      {visible.map((item) => (
        <div key={item.key} className="flex items-baseline justify-between gap-4 sm:gap-8">
          <dt className="min-w-0 text-[13px] text-muted-foreground">{item.key}</dt>
          <dd className="max-w-[60%] shrink-0 text-right text-[13px] break-words">{item.value}</dd>
        </div>
      ))}
    </dl>
  )
}
